import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { ArrowLeft, Key, Monitor, Mail, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  description: string;
  path: string;
}

const MENU_ITEMS: MenuItem[] = [
  { id: 'password', label: 'Change Password', icon: Key, description: 'Update your account password', path: '/security/password' },
  { id: 'sessions', label: 'Active Sessions', icon: Monitor, description: 'View and manage your logged-in devices', path: '/security/sessions' },
  { id: 'email', label: 'Change Email', icon: Mail, description: 'Update your email address', path: '/security/email' },
];

export function SecurityPage() {
  useDocumentTitle('Privacy & Security');
  const navigate = useNavigate();

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>

          <h1 style={styles.title}>Privacy & Security</h1>

          <div style={styles.menuSection}>
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  style={styles.menuItem}
                  onClick={() => navigate(item.path)}
                >
                  <div style={styles.menuIcon}>
                    <Icon size={20} color={colors.gray[600]} />
                  </div>
                  <div style={styles.menuContent}>
                    <h3 style={styles.menuLabel}>{item.label}</h3>
                    <p style={styles.menuDescription}>{item.description}</p>
                  </div>
                  <ChevronRight size={20} color={colors.gray[300]} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: 24,
  },
  menuSection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${colors.gray[100]}`,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.2s',
  },
  menuIcon: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
  },
  menuContent: {
    flex: 1,
  },
  menuLabel: {
    fontSize: '15px',
    fontWeight: 500,
    color: colors.ink.black,
    marginBottom: '2px',
  },
  menuDescription: {
    fontSize: '13px',
    color: colors.gray[600],
  },
};
